import { useCallback, useEffect, useState } from 'react';
import useFetch, { CachePolicies } from 'use-http';
import { convertUser, convertUserFilter, extractManagers } from 'utils/helpers';
import { ISelectOptions } from 'types/utils';
import { REGISTRATION_TYPE_OPTIONS } from '../constants';
import { TableFilter } from 'components/UI/Table';

export const useSessionsData = (storeId?: string, filter?: TableFilter) => {
	const { get } = useFetch({ cachePolicy: CachePolicies.NO_CACHE });

	const [userList, setUserList] = useState([]);
	const [isLoadingUsers, setIsLoadingUsers] = useState(false);
	const [storesList, setStoresList] = useState([]);
	const [registrationTypeOptions] = useState(REGISTRATION_TYPE_OPTIONS);
	const [regionalManagersOptions, setRegionalManagersOptions] = useState<{ label: string; value: string }[]>([]);
	const [aoManagersOptions, setAoManagersOptions] = useState<{ label: string; value: string }[]>([]);

	const receiveUsers = useCallback(async () => {
		setIsLoadingUsers(true);
		try {
			const params = new URLSearchParams();
			if (storeId) {
				params.set('storeId', storeId);
			}
			if (filter?.fromDate) {
				params.set('fromDate', filter.fromDate.toString());
			}
			if (filter?.toDate) {
				params.set('toDate', filter.toDate.toString());
			}
			
			const usersFio = await get(`/api/session/usersByStore?${params.toString()}`);
			setUserList(usersFio || []);
		} catch (error) {
			console.error('Error receiving users:', error);
			setUserList([]);
		} finally {
			setIsLoadingUsers(false);
		}
	}, [storeId, filter?.fromDate, filter?.toDate, get]);

	const init = useCallback(async () => {
		try {
			const resultStores = await get('/api/stores');

			if (resultStores?.rows) {
				const { regionalManagers, aoManagers } = extractManagers(resultStores.rows);

				setRegionalManagersOptions(regionalManagers);
				setAoManagersOptions(aoManagers);
				setStoresList(resultStores.rows);
			}
			
			await receiveUsers();
		} catch (error) {
			console.error('Error initializing sessions data:', error);
		}
	}, [receiveUsers, get]);

	useEffect(() => {
		init();
	}, [init]);

	useEffect(() => {
		receiveUsers();
	}, [filter?.fromDate, filter?.toDate, storeId, receiveUsers]);

	const loadUser = useCallback(
		async (inputValue: string): Promise<ISelectOptions[]> => {
			try {
				let data = [];
				const params = new URLSearchParams();
				
				if (inputValue) {
					params.set('fio', inputValue);
				}
				if (storeId) {
					params.set('storeId', storeId);
				}
				if (filter?.fromDate) {
					params.set('fromDate', filter.fromDate.toString());
				}
				if (filter?.toDate) {
					params.set('toDate', filter.toDate.toString());
				}
				
				if (inputValue) {
					data = await get(`/api/session/usersByStore?${params.toString()}`);
				}

				return convertUserFilter(data);
			} catch (error) {
				console.error('Error loading users:', error);
				return [];
			}
		},
		[storeId, filter?.fromDate, filter?.toDate, get],
	);

	return {
		userList,
		isLoadingUsers,
		storesList,
		registrationTypeOptions,
		regionalManagersOptions,
		aoManagersOptions,
		loadUser,
		init,
	};
};
